import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Checkbox, Divider, Group, Modal, NumberInput, Paper, Select, SimpleGrid, Stack, Switch,
  Table, Tabs, Text, Textarea, TextInput, Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconArrowLeft, IconCalendarPlus, IconCheck, IconCopy, IconEdit, IconPlus, IconTrash, IconListCheck, IconTimeline,
  IconBook2, IconArchive, IconRestore,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { Empty, PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import { useItems } from "../orchard/DiaryTab";
import {
  fmtMd, type Issue, type Procedure, type Product, productLine, type Step, TYPE_COLOR, TYPE_SHORT, YearTimeline,
} from "./common";

const EMPTY_STEP: Step = { faza: "", od: "03-01", do: "03-15", vrsta: "zastita", naziv: "", opis: "", sredstva: [], uslov: "", obavezno: true };

function StepModal({ step, species, onSave, onClose }: { step: Step; species: string; onSave: (s: Step) => void; onClose: () => void }) {
  const { t } = useT();
  const types = useQuery({ queryKey: ["entry-types"], queryFn: () => http.get<{ code: string; name: string }[]>("/api/v1/diary/entry-types") });
  const ffs = useItems("ffs"), fert = useItems("dubrivo");
  const [s, setS] = useState<Step>({ ...step, sredstva: [...(step.sredstva ?? [])] });
  const set = (patch: Partial<Step>) => setS((x) => ({ ...x, ...patch }));
  const setP = (i: number, patch: Partial<Product>) => set({ sredstva: s.sredstva.map((p, j) => (i === j ? { ...p, ...patch } : p)) });
  const ffsData = useMemo(() => (ffs.data ?? []).map((x) => {
    const k = (x.attributes.kulture as string[] | undefined) ?? [];
    return { value: x.code, label: `${x.name}${k.length && !k.includes(species) ? ` (${t("nije registrovano za vrstu")})` : ""}` };
  }), [ffs.data, species, t]);
  const md = (v: string) => /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v);
  const ok = s.naziv.trim().length > 1 && md(s.od) && md(s.do);
  return (
    <Modal opened onClose={onClose} size="xl" title={step.naziv ? `${t("Korak")}: ${step.naziv}` : t("Novi korak")}>
      <Stack>
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <TextInput label={t("Naziv koraka")} required value={s.naziv} onChange={(e) => set({ naziv: e.currentTarget.value })} />
          <Select label={t("Radna operacija (poljoprivredna knjiga)")} data={(types.data ?? []).map((x) => ({ value: x.code, label: x.name }))}
            value={s.vrsta} onChange={(v) => set({ vrsta: v ?? "ostalo" })} allowDeselect={false} />
          <TextInput label={t("Faza")} placeholder={t("npr. Ružičasti pupoljak")} value={s.faza} onChange={(e) => set({ faza: e.currentTarget.value })} />
          <TextInput label={t("Od (MM-DD)")} required value={s.od} error={!md(s.od)} onChange={(e) => set({ od: e.currentTarget.value })}
            description={md(s.od) ? fmtMd(s.od) : t("format 04-15")} />
          <TextInput label={t("Do (MM-DD)")} required value={s.do} error={!md(s.do)} onChange={(e) => set({ do: e.currentTarget.value })}
            description={md(s.do) ? fmtMd(s.do) : t("format 04-30")} />
          <Group grow>
            <NumberInput label="BBCH od" min={0} max={99} value={s.bbch_od ?? ""} onChange={(v) => set({ bbch_od: v === "" ? null : Number(v) })} />
            <NumberInput label="BBCH do" min={0} max={99} value={s.bbch_do ?? ""} onChange={(v) => set({ bbch_do: v === "" ? null : Number(v) })} />
          </Group>
          <NumberInput label={t("Ponoviti svakih (dana)")} min={1} max={120} value={s.ponavljanje_dana ?? ""}
            onChange={(v) => set({ ponavljanje_dana: v === "" ? null : Number(v) })} />
          <NumberInput label={t("Broj ponavljanja")} min={1} max={20} value={s.ponavljanja ?? ""}
            onChange={(v) => set({ ponavljanja: v === "" ? null : Number(v) })} description={t("prazno = koliko stane u prozor")} />
          <NumberInput label={t("Utrošak vode (l/ha)")} min={0} max={3000} value={s.voda_l_ha ?? ""}
            onChange={(v) => set({ voda_l_ha: v === "" ? null : Number(v) })} description={t("za doze u %")} />
        </SimpleGrid>
        <Textarea label={t("Uslov (kada se radi)")} autosize minRows={1} placeholder={t("npr. prag: 2–3 leptira po klopci sedmično")}
          value={s.uslov ?? ""} onChange={(e) => set({ uslov: e.currentTarget.value })} />
        <Textarea label={t("Uputstvo / objašnjenje")} autosize minRows={2} value={s.opis ?? ""} onChange={(e) => set({ opis: e.currentTarget.value })} />
        <Switch label={t("Obavezan korak (ako nije — radi se samo kad je uslov ispunjen)")} checked={s.obavezno}
          onChange={(e) => set({ obavezno: e.currentTarget.checked })} />
        {s.vrsta === "navodnjavanje" && (
          <NumberInput label={t("Norma po zalivanju (m³/ha)")} w={260} value={s.navodnjavanje?.kolicina_m3_ha ?? ""}
            onChange={(v) => set({ navodnjavanje: { ...(s.navodnjavanje ?? {}), sistem: "KAP", kolicina_m3_ha: v === "" ? undefined : Number(v) } })} />
        )}
        {(s.vrsta === "zastita" || s.vrsta === "dubrenje") && (
          <>
            <Divider label={s.vrsta === "zastita" ? t("Sredstva za zaštitu bilja") : t("Đubriva")} />
            {s.sredstva.map((p, i) => (
              <Card key={i} withBorder p="xs">
                <SimpleGrid cols={{ base: 1, sm: 4 }}>
                  <Select label={p.vrsta === "ffs" ? t("Sredstvo (šifarnik)") : t("Đubrivo (šifarnik)")} searchable clearable
                    data={p.vrsta === "ffs" ? ffsData : (fert.data ?? []).map((x) => ({ value: x.code, label: x.name }))}
                    value={p.sifra ?? null} onChange={(v) => {
                      const it = (p.vrsta === "ffs" ? ffs.data : fert.data)?.find((x) => x.code === v);
                      setP(i, { sifra: v, naziv: it?.name ?? p.naziv });
                    }} />
                  <TextInput label={t("Naziv")} value={p.naziv} onChange={(e) => setP(i, { naziv: e.currentTarget.value })} />
                  <Group grow gap={6}>
                    <NumberInput label={t("Doza")} decimalScale={4} decimalSeparator="," value={p.doza ?? ""}
                      onChange={(v) => setP(i, { doza: v === "" ? null : Number(v) })} />
                    <Select label={t("Jedinica")} data={p.vrsta === "ffs" ? ["%", "l/ha", "kg/ha"] : ["kg/ha", "l/ha", "t/ha"]}
                      value={p.jedinica ?? null} onChange={(v) => setP(i, { jedinica: v })} />
                  </Group>
                  {p.vrsta === "ffs"
                    ? <Group grow gap={6}>
                        <TextInput label={t("Cilj")} value={p.cilj ?? ""} onChange={(e) => setP(i, { cilj: e.currentTarget.value })} />
                        <NumberInput label={t("Karenca")} value={p.karenca_dana ?? ""} onChange={(v) => setP(i, { karenca_dana: v === "" ? null : Number(v) })} />
                      </Group>
                    : <TextInput label={t("Napomena")} value={p.napomena ?? ""} onChange={(e) => setP(i, { napomena: e.currentTarget.value })} />}
                </SimpleGrid>
                <Button mt={4} size="compact-xs" color="red" variant="subtle" leftSection={<IconTrash size={12} />}
                  onClick={() => set({ sredstva: s.sredstva.filter((_, j) => j !== i) })}>{t("Ukloni")}</Button>
              </Card>
            ))}
            <Button variant="subtle" size="xs" w={200} leftSection={<IconPlus size={14} />}
              onClick={() => set({ sredstva: [...s.sredstva, { vrsta: s.vrsta === "zastita" ? "ffs" : "dubrivo", naziv: "", jedinica: s.vrsta === "zastita" ? "%" : "kg/ha" }] })}>
              {s.vrsta === "zastita" ? t("Dodaj sredstvo") : t("Dodaj đubrivo")}
            </Button>
          </>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t("Odustani")}</Button>
          <Button disabled={!ok} onClick={() => onSave(s)}>{t("Primijeni korak")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ApplyModal({ proc, onClose }: { proc: Procedure; onClose: () => void }) {
  const { t } = useT();
  const orchards = useQuery({ queryKey: ["orchards-min"], queryFn: () => http.get<{ id: number; name: string; species: string; species_name?: string; producer?: string }[]>("/api/v1/orchards") });
  const [oid, setOid] = useState<string | null>(null);
  const [season, setSeason] = useState<number | string>(new Date().getFullYear());
  const [part, setPart] = useState("");
  const [shift, setShift] = useState<number | string>(0);
  const [replace, setReplace] = useState(false);
  const go = useMutation({
    mutationFn: () => http.post<{ created: number }>(`/api/v1/orchards/${oid}/plan`, {
      procedure_id: proc.id, season: Number(season), part_of_orchard: part || null, shift_days: Number(shift) || 0, replace }),
    onSuccess: (r) => { notifications.show({ color: "green", message: `${t("Plan radova napravljen — stavki")}: ${r.created}` }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const list = (orchards.data ?? []).sort((a, b) => Number(b.species === proc.species_code) - Number(a.species === proc.species_code));
  return (
    <Modal opened onClose={onClose} title={`${t("Primjena na voćnjak")}: ${proc.name}`} size="lg">
      <Stack>
        <Select label={t("Voćnjak")} searchable required value={oid} onChange={setOid}
          data={list.map((o) => ({ value: String(o.id), label: `${o.name}${o.producer ? ` · ${o.producer}` : ""}${o.species !== proc.species_code ? ` (${o.species_name ?? o.species})` : ""}` }))} />
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <NumberInput label={t("Sezona")} min={2000} max={2100} value={season} onChange={setSeason} />
          <NumberInput label={t("Pomjeranje rokova (dana)")} min={-60} max={60} value={shift} onChange={setShift}
            description={t("+7 do +14 za više predjele")} />
          <TextInput label={t("Dio voćnjaka")} placeholder={t("npr. redovi 1–4 (kruška)")} value={part} onChange={(e) => setPart(e.currentTarget.value)} />
        </SimpleGrid>
        <Checkbox label={t("Zamijeni postojeće neurađene stavke ovog postupka za sezonu")} checked={replace} onChange={(e) => setReplace(e.currentTarget.checked)} />
        <Text size="xs" c="dimmed">{t("Svaki korak postaje stavka plana sa rokom. Urađene stavke se upisuju u poljoprivrednu knjigu iz kartice voćnjaka (Plan radova → Upiši u knjigu).")}</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t("Odustani")}</Button>
          <Button disabled={!oid} loading={go.isPending} leftSection={<IconCalendarPlus size={16} />} onClick={() => go.mutate()}>{t("Napravi plan")}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ProcedureDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const can = useCan();
  const q = useQuery({ queryKey: ["procedure", id], queryFn: () => http.get<Procedure & { steps: Step[] }>(`/api/v1/procedures/${id}`) });
  const check = useQuery({ queryKey: ["procedure-check", id, q.data?.version], enabled: !!q.data,
    queryFn: () => http.get<Issue[]>(`/api/v1/procedures/${id}/check`) });
  const species = useItems("vrsta_voca");
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState<Procedure & { steps: Step[] } | null>(null);
  const [stepEdit, setStepEdit] = useState<{ i: number; s: Step } | null>(null);
  const [copy, setCopy] = useState(false);
  const [apply, setApply] = useState(false);
  const [pick, setPick] = useState<Step | null>(null);
  useEffect(() => { if (q.data && !edit) setDraft(q.data); }, [q.data, edit]);
  const save = useMutation({
    mutationFn: () => http.put(`/api/v1/procedures/${id}`, { name: draft!.name, species_code: draft!.species_code, cultivar: draft!.cultivar || null,
      description: draft!.description || null, steps: draft!.steps }),
    onSuccess: () => { notifications.show({ color: "green", message: t("Postupak sačuvan (nova verzija)") }); setEdit(false); qc.invalidateQueries({ queryKey: ["procedure", id] }); qc.invalidateQueries({ queryKey: ["procedures"] }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const toggle = useMutation({
    mutationFn: (active: boolean) => http.patch(`/api/v1/procedures/${id}/active`, undefined, { active }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["procedure", id] }); qc.invalidateQueries({ queryKey: ["procedures"] }); },
  });
  const del = useMutation({
    mutationFn: () => http.del(`/api/v1/procedures/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["procedures"] }); onBack(); },
  });
  if (!q.data || !draft) return <Text c="dimmed">{t("Učitavanje...")}</Text>;
  const p = q.data;
  const steps = edit ? draft.steps : p.steps;
  const issues = check.data ?? [];
  const byStep = (sid?: string) => issues.filter((i) => i.step === sid);
  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <div>
          <Anchor size="sm" onClick={onBack}><Group gap={4}><IconArrowLeft size={14} />{t("Svi postupci")}</Group></Anchor>
          {edit ? (
            <SimpleGrid cols={{ base: 1, sm: 3 }} mt="xs">
              <TextInput label={t("Naziv")} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })} />
              <Select label={t("Vrsta voća")} data={(species.data ?? []).map((x) => ({ value: x.code, label: x.name }))} value={draft.species_code}
                onChange={(v) => setDraft({ ...draft, species_code: v ?? draft.species_code })} />
              <TextInput label={t("Sorta")} value={draft.cultivar ?? ""} onChange={(e) => setDraft({ ...draft, cultivar: e.currentTarget.value })} />
            </SimpleGrid>
          ) : (
            <Title order={3} mt={4}>{p.name}</Title>
          )}
          <Group gap={6} mt={6}>
            <Badge variant="light">{p.species_name}</Badge>
            {p.cultivar && <Badge variant="light" color="grape" data-no-translit="true">{p.cultivar}</Badge>}
            <Badge variant="outline" color={p.owner ? "gray" : "teal"}>{p.owner ? t("Lični postupak") : t("Postupak Agencije")}</Badge>
            {p.builtin && <Badge variant="outline" color="blue">{t("ugrađen")}</Badge>}
            <Badge variant="outline">v{p.version}</Badge>
            {p.based_on && <Badge variant="outline" color="gray">{t("kopija od")} {p.based_on}</Badge>}
            {!p.active && <Badge color="red">{t("povučen")}</Badge>}
          </Group>
        </div>
        <Group gap="xs">
          {edit ? (
            <>
              <Button variant="default" onClick={() => { setEdit(false); setDraft(q.data); }}>{t("Odustani")}</Button>
              <Button leftSection={<IconCheck size={16} />} loading={save.isPending} onClick={() => save.mutate()}>{t("Sačuvaj postupak")}</Button>
            </>
          ) : (
            <>
              {can("diary:write") && <Button variant="light" leftSection={<IconCalendarPlus size={16} />} onClick={() => setApply(true)}>{t("Primijeni na voćnjak")}</Button>}
              {can("diary:write") && <Button variant="default" leftSection={<IconCopy size={16} />} onClick={() => setCopy(true)}>{t("Kopiraj")}</Button>}
              {p.editable && <Button leftSection={<IconEdit size={16} />} onClick={() => setEdit(true)}>{t("Uredi")}</Button>}
              {p.editable && (p.active
                ? <Tooltip label={t("Povučen postupak se ne nudi za nove planove; postojeći planovi ostaju")}><ActionIcon variant="default" size="lg" onClick={() => toggle.mutate(false)}><IconArchive size={16} /></ActionIcon></Tooltip>
                : <Tooltip label={t("Ponovo aktiviraj")}><ActionIcon variant="default" size="lg" onClick={() => toggle.mutate(true)}><IconRestore size={16} /></ActionIcon></Tooltip>)}
              {p.owner && p.editable && <Tooltip label={t("Obriši lični postupak")}><ActionIcon color="red" variant="light" size="lg" onClick={() => del.mutate()}><IconTrash size={16} /></ActionIcon></Tooltip>}
            </>
          )}
        </Group>
      </Group>
      {edit ? (
        <Textarea label={t("Opis")} autosize minRows={2} value={draft.description ?? ""} onChange={(e) => setDraft({ ...draft, description: e.currentTarget.value })} />
      ) : p.description && <Text size="sm">{p.description}</Text>}
      {!p.editable && !edit && (
        <Alert color="blue" variant="light">
          {t("Ovo je postupak Agencije — ne možete ga mijenjati, ali ga možete kopirati u svoj postupak i prilagoditi voćnjaku (Kopiraj → Uredi).")}
        </Alert>
      )}
      {issues.some((i) => i.level !== "info") && (
        <Alert color="orange" icon={<IconAlertTriangle />} title={t("Provjera prema šifarnicima")}>
          {issues.filter((i) => i.level !== "info").map((i, k) => <Text key={k} size="sm">• {i.msg}</Text>)}
        </Alert>
      )}
      <Tabs defaultValue="kalendar" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="kalendar" leftSection={<IconTimeline size={15} />}>{t("Godišnji kalendar")}</Tabs.Tab>
          <Tabs.Tab value="koraci" leftSection={<IconListCheck size={15} />}>{t("Koraci")} ({steps.length})</Tabs.Tab>
          <Tabs.Tab value="kako" leftSection={<IconBook2 size={15} />}>{t("Kako mijenjati")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="kalendar" pt="md">
          <Paper withBorder p="md" radius="md"><YearTimeline steps={steps} issues={issues} onPick={(s) => (edit ? setStepEdit({ i: steps.indexOf(s), s }) : setPick(s))} /></Paper>
        </Tabs.Panel>
        <Tabs.Panel value="koraci" pt="md">
          <Table striped highlightOnHover verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr><Table.Th w={120}>{t("Rok")}</Table.Th><Table.Th>{t("Korak")}</Table.Th><Table.Th>{t("Sredstva / norma")}</Table.Th><Table.Th w={edit ? 110 : 0} /></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {steps.map((s, i) => (
                <Table.Tr key={s.id ?? i} onClick={() => !edit && setPick(s)} style={{ cursor: edit ? undefined : "pointer" }}>
                  <Table.Td>
                    <Text size="xs">{fmtMd(s.od)} – {fmtMd(s.do)}</Text>
                    {s.bbch_od != null && <Text size="xs" c="dimmed">BBCH {s.bbch_od}{s.bbch_do !== s.bbch_od ? `–${s.bbch_do}` : ""}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Badge size="xs" color={TYPE_COLOR[s.vrsta] ?? "gray"} variant={s.obavezno ? "filled" : "outline"}>{t(TYPE_SHORT[s.vrsta] ?? s.vrsta)}</Badge>
                      <Text size="sm" fw={500}>{s.naziv}</Text>
                    </Group>
                    {s.faza && <Text size="xs" c="dimmed">{s.faza}</Text>}
                    {s.uslov && <Text size="xs" c="orange.8">{t("Uslov")}: {s.uslov}</Text>}
                    {byStep(s.id).map((x, k) => <Text key={k} size="xs" c={x.level === "info" ? "dimmed" : "orange"}>⚠ {x.msg}</Text>)}
                  </Table.Td>
                  <Table.Td>
                    {s.sredstva.map((m, k) => <Text key={k} size="xs" data-no-translit={m.vrsta === "ffs" ? "true" : undefined}>{productLine(m)}</Text>)}
                    {s.navodnjavanje?.kolicina_m3_ha && <Text size="xs">{s.navodnjavanje.kolicina_m3_ha} m³/ha</Text>}
                    {(s.ponavljanja || s.ponavljanje_dana) && <Text size="xs" c="dimmed">{s.ponavljanja ? `${s.ponavljanja}× ` : ""}{s.ponavljanje_dana ? `${t("svakih")} ${s.ponavljanje_dana} ${t("dana")}` : ""}</Text>}
                  </Table.Td>
                  <Table.Td>
                    {edit && (
                      <Group gap={2} wrap="nowrap">
                        <ActionIcon variant="subtle" onClick={() => setStepEdit({ i, s })} aria-label={t("Uredi")}><IconEdit size={15} /></ActionIcon>
                        <ActionIcon variant="subtle" onClick={() => setDraft({ ...draft, steps: [...draft.steps.slice(0, i + 1), { ...s, id: undefined, naziv: `${s.naziv} (kopija)` }, ...draft.steps.slice(i + 1)] })}
                          aria-label={t("Kopiraj")}><IconCopy size={15} /></ActionIcon>
                        <ActionIcon variant="subtle" color="red" onClick={() => setDraft({ ...draft, steps: draft.steps.filter((_, j) => j !== i) })} aria-label={t("Ukloni")}><IconTrash size={15} /></ActionIcon>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {edit && <Button mt="sm" variant="light" leftSection={<IconPlus size={16} />} onClick={() => setStepEdit({ i: -1, s: EMPTY_STEP })}>{t("Dodaj korak")}</Button>}
        </Tabs.Panel>
        <Tabs.Panel value="kako" pt="md"><HowTo /></Tabs.Panel>
      </Tabs>
      {stepEdit && (
        <StepModal step={stepEdit.s} species={draft.species_code} onClose={() => setStepEdit(null)} onSave={(s) => {
          const steps2 = stepEdit.i < 0 ? [...draft.steps, s] : draft.steps.map((x, j) => (j === stepEdit.i ? s : x));
          setDraft({ ...draft, steps: [...steps2].sort((a, b) => (a.od + a.do).localeCompare(b.od + b.do)) });
          setStepEdit(null);
        }} />
      )}
      {pick && (
        <Modal opened onClose={() => setPick(null)} title={pick.naziv} size="lg">
          <Stack gap={6}>
            <Group gap={6}><Badge color={TYPE_COLOR[pick.vrsta] ?? "gray"}>{t(TYPE_SHORT[pick.vrsta] ?? pick.vrsta)}</Badge>
              <Text size="sm">{fmtMd(pick.od)} – {fmtMd(pick.do)}{pick.bbch_od != null ? ` · BBCH ${pick.bbch_od}–${pick.bbch_do}` : ""}</Text>
              <Badge variant="outline">{pick.obavezno ? t("obavezno") : t("po potrebi")}</Badge></Group>
            {pick.faza && <Text size="sm" c="dimmed">{pick.faza}</Text>}
            {pick.uslov && <Alert color="orange" variant="light" p="xs">{t("Uslov")}: {pick.uslov}</Alert>}
            {pick.opis && <Text size="sm">{pick.opis}</Text>}
            {pick.sredstva.length > 0 && <Divider label={t("Sredstva")} />}
            {pick.sredstva.map((m, k) => <Text key={k} size="sm" data-no-translit={m.vrsta === "ffs" ? "true" : undefined}>• {productLine(m)}{m.napomena ? ` — ${m.napomena}` : ""}</Text>)}
            {pick.voda_l_ha && <Text size="xs" c="dimmed">{t("Utrošak vode")}: {pick.voda_l_ha} l/ha</Text>}
          </Stack>
        </Modal>
      )}
      {copy && <CopyModal proc={p} onClose={() => setCopy(false)} />}
      {apply && <ApplyModal proc={p} onClose={() => setApply(false)} />}
    </Stack>
  );
}

function CopyModal({ proc, onClose }: { proc: Procedure; onClose: () => void }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const [, setParams] = useSearchParams();
  const [name, setName] = useState(`${proc.name} (kopija)`);
  const [cultivar, setCultivar] = useState(proc.cultivar ?? "");
  const [agency, setAgency] = useState(false);
  const go = useMutation({
    mutationFn: () => http.post<{ id: number }>(`/api/v1/procedures/${proc.id}/copy`, { name, cultivar, agency }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["procedures"] }); onClose(); setParams({ id: String(r.id) }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={t("Kopija postupka")}>
      <Stack>
        <TextInput label={t("Naziv nove kopije")} value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <TextInput label={t("Sorta")} value={cultivar} onChange={(e) => setCultivar(e.currentTarget.value)}
          description={t("Npr. kopija postupka za Idared kao osnova za Jonagold")} />
        {can("procedures:manage") && <Checkbox label={t("Postupak Agencije (vidljiv svim korisnicima)")} checked={agency} onChange={(e) => setAgency(e.currentTarget.checked)} />}
        <Group justify="flex-end"><Button loading={go.isPending} onClick={() => go.mutate()}>{t("Kopiraj i otvori")}</Button></Group>
      </Stack>
    </Modal>
  );
}

function NewModal({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const [, setParams] = useSearchParams();
  const species = useItems("vrsta_voca");
  const [f, setF] = useState({ name: "", species_code: "JABUKA", cultivar: "", description: "", agency: false });
  const go = useMutation({
    mutationFn: () => http.post<{ id: number }>("/api/v1/procedures", { ...f, cultivar: f.cultivar || null, steps: [] }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["procedures"] }); onClose(); setParams({ id: String(r.id) }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={t("Novi tehnološki postupak")}>
      <Stack>
        <Text size="sm" c="dimmed">{t("Najlakše je kopirati postojeći postupak iste vrste i izmijeniti ga. Prazan postupak popunjavate korak po korak (Uredi → Dodaj korak).")}</Text>
        <TextInput label={t("Naziv")} required value={f.name} onChange={(e) => setF({ ...f, name: e.currentTarget.value })} />
        <Select label={t("Vrsta voća")} data={(species.data ?? []).map((x) => ({ value: x.code, label: x.name }))} value={f.species_code}
          onChange={(v) => setF({ ...f, species_code: v ?? "JABUKA" })} allowDeselect={false} />
        <TextInput label={t("Sorta")} value={f.cultivar} onChange={(e) => setF({ ...f, cultivar: e.currentTarget.value })} />
        <Textarea label={t("Opis")} value={f.description} onChange={(e) => setF({ ...f, description: e.currentTarget.value })} />
        {can("procedures:manage") && <Checkbox label={t("Postupak Agencije")} checked={f.agency} onChange={(e) => setF({ ...f, agency: e.currentTarget.checked })} />}
        <Group justify="flex-end"><Button disabled={f.name.trim().length < 3} loading={go.isPending} onClick={() => go.mutate()}>{t("Napravi")}</Button></Group>
      </Stack>
    </Modal>
  );
}

function HowTo() {
  const { t } = useT();
  return (
    <Stack gap="xs" maw={860}>
      <Text size="sm"><b>{t("Šta je postupak.")}</b> {t("Godišnji redoslijed radova za vrstu i sortu: svaki korak ima fazu (BBCH), okvirni rok za područje Banje Luke, vrstu operacije iz poljoprivredne knjige, sredstva sa dozom i uslov kada se radi. Postupak je predložak — iz njega se za voćnjak i sezonu pravi plan radova.")}</Text>
      <Text size="sm"><b>{t("Prilagođavanje voćnjaku.")}</b> {t("Kopiraj → u kopiji Uredi. Promijenite rokove (npr. +10 dana za zasade iznad 500 m), zamijenite sredstvo drugim iz šifarnika, dodajte ili uklonite korak. Pri primjeni na voćnjak rokove možete pomjeriti i poljem „Pomjeranje rokova“ bez izmjene postupka.")}</Text>
      <Text size="sm"><b>{t("Primjer — druga sorta.")}</b> {t("Za Jonagold kopirajte „Jabuka — Idared“, u kopiji upišite sortu Jonagold, pomjerite berbu na 15.–30. septembar i uklonite dodatne tretmane protiv pepelnice (Jonagold je manje osjetljiv).")}</Text>
      <Text size="sm"><b>{t("Primjer — organska proizvodnja.")}</b> {t("U kopiji uklonite herbicid i sintetička sredstva, a umjesto njih dodajte dozvoljena (bakar, sumpor, ulje, granulovirus, Bacillus) i mehaničku obradu reda.")}</Text>
      <Text size="sm"><b>{t("Provjera.")}</b> {t("Aplikacija svaki postupak provjerava prema šifarniku FFS: sredstvo nije registrovano za vrstu, registracija je istekla ili ističe, doza je veća od etikete. Upozorenja se vide iznad kalendara i uz korak.")}</Text>
      <Text size="sm"><b>{t("Izvršenje.")}</b> {t("U kartici voćnjaka → Plan radova svaka stavka ima „Upiši u knjigu“: otvara upis sa popunjenim sredstvima i dozama preračunatim po hektaru, a stavka postaje urađena. Stavke koje nisu potrebne (uslov nije ispunjen) označavaju se kao preskočene.")}</Text>
      <Text size="sm"><b>{t("Postupci Agencije")}</b> {t("mijenja korisnik sa ovlašćenjem „Tehnološki postupci Agencije“; svaka izmjena povećava verziju. Ugrađeni postupci se ažuriraju sa novim izdanjem aplikacije sve dok ih niko ne izmijeni.")}</Text>
      <Anchor component={Link} to="/uputstvo#g45-tehnoloski-postupci-i-plan-radova" size="sm">{t("Detaljno uputstvo sa primjerima")}</Anchor>
    </Stack>
  );
}

export default function ProceduresPage() {
  const { t } = useT();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const [sp, setSp] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [creating, setCreating] = useState(false);
  const species = useItems("vrsta_voca");
  const q = useQuery({ queryKey: ["procedures", sp, all], queryFn: () => http.get<Procedure[]>("/api/v1/procedures", { species: sp, all }) });
  const id = Number(params.get("id")) || null;
  if (id) return <ProcedureDetail id={id} onBack={() => setParams({})} />;
  const groups = new Map<string, Procedure[]>();
  for (const p of q.data ?? []) groups.set(p.species_name ?? p.species_code, [...(groups.get(p.species_name ?? p.species_code) ?? []), p]);
  return (
    <Stack>
      <PageHeader title="Tehnološki postupci"
        description="Godišnji plan radova po vrsti i sorti: zaštita (sredstva iz Jedinstvenog spiska FFS), ishrana, navodnjavanje, rezidba, berba. Postupak se primjenjuje na voćnjak i postaje plan radova sezone."
        right={can("diary:write") ? <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>{t("Novi postupak")}</Button> : undefined} />
      <Group>
        <Select placeholder={t("Sve vrste")} clearable data={(species.data ?? []).map((x) => ({ value: x.code, label: x.name }))} value={sp} onChange={setSp} w={220} />
        <Switch label={t("Prikaži i povučene")} checked={all} onChange={(e) => setAll(e.currentTarget.checked)} />
      </Group>
      {!q.data?.length && !q.isLoading && <Empty text="Nema postupaka" />}
      {[...groups.entries()].map(([g, list]) => (
        <div key={g}>
          <Title order={5} mb="xs">{g}</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {list.map((p) => (
              <Card key={p.id} withBorder radius="md" p="md" style={{ cursor: "pointer", opacity: p.active ? 1 : 0.6 }} onClick={() => setParams({ id: String(p.id) })}>
                <Text fw={600}>{p.name}</Text>
                <Group gap={6} mt={6}>
                  {p.cultivar && <Badge size="sm" variant="light" color="grape" data-no-translit="true">{p.cultivar}</Badge>}
                  <Badge size="sm" variant="outline" color={p.owner ? "gray" : "teal"}>{p.owner ? t("lični") : t("Agencija")}</Badge>
                  <Badge size="sm" variant="outline">{p.steps as number} {t("koraka")}</Badge>
                  <Badge size="sm" variant="outline" color="red">{p.zastita} {t("tretmana")}</Badge>
                  {!!p.orchards && <Badge size="sm" variant="outline" color="green">{p.orchards} {t("voćnjaka")}</Badge>}
                  {!p.active && <Badge size="sm" color="red">{t("povučen")}</Badge>}
                </Group>
                {p.description && <Text size="xs" c="dimmed" mt={6} lineClamp={2}>{p.description}</Text>}
              </Card>
            ))}
          </SimpleGrid>
        </div>
      ))}
      {creating && <NewModal onClose={() => setCreating(false)} />}
    </Stack>
  );
}
